import { useRef, useState, useEffect } from 'react';
import * as THREE from 'three';

export function Lock3D({ lockState }: any) {
  const groupRef = useRef<THREE.Group>(null);
  const cylinderRef = useRef<THREE.Group>(null);

  useEffect(() => {
    if (groupRef.current) {
      groupRef.current.rotation.z = lockState.rotation * 0.05;
    }
  }, [lockState.rotation]);

  return (
    <group ref={groupRef}>
      {/* Lock Housing */}
      <mesh position={[0, 0, 0]} castShadow>
        <boxGeometry args={[2.5, 3.5, 1.2]} />
        <meshStandardMaterial
          color="#1a1a1a"
          metalness={0.8}
          roughness={0.2}
        />
      </mesh>

      {/* Lock Face */}
      <mesh position={[0, 0, 0.61]} castShadow>
        <boxGeometry args={[2.3, 3.3, 0.1]} />
        <meshStandardMaterial
          color="#0f0f0f"
          metalness={0.9}
          roughness={0.1}
        />
      </mesh>

      {/* Transparent Housing */}
      <mesh position={[0, 0, 0]} castShadow>
        <boxGeometry args={[2.8, 3.8, 1.5]} />
        <meshStandardMaterial
          color="#ffffff"
          metalness={0.1}
          roughness={0.05}
          transparent
          opacity={0.15}
          side={THREE.DoubleSide}
        />
      </mesh>

      {/* Housing Frame (opaque edges) */}
      <lineSegments>
        <bufferGeometry>
          <bufferAttribute
            attach="attributes-position"
            count={24}
            array={new Float32Array([
              -1.4, -1.9, -0.75, 1.4, -1.9, -0.75,
              1.4, -1.9, -0.75, 1.4, 1.9, -0.75,
              1.4, 1.9, -0.75, -1.4, 1.9, -0.75,
              -1.4, 1.9, -0.75, -1.4, -1.9, -0.75,
              -1.4, -1.9, 0.75, 1.4, -1.9, 0.75,
              1.4, -1.9, 0.75, 1.4, 1.9, 0.75,
              1.4, 1.9, 0.75, -1.4, 1.9, 0.75,
              -1.4, 1.9, 0.75, -1.4, -1.9, 0.75,
              -1.4, -1.9, -0.75, -1.4, -1.9, 0.75,
              1.4, -1.9, -0.75, 1.4, -1.9, 0.75,
              1.4, 1.9, -0.75, 1.4, 1.9, 0.75,
              -1.4, 1.9, -0.75, -1.4, 1.9, 0.75,
            ])}
            itemSize={3}
          />
        </bufferGeometry>
        <lineBasicMaterial color="#888888" linewidth={2} />
      </lineSegments>
      {/* Cylinder (rotating part) */}
      <group ref={cylinderRef} position={[0, 0, 0]}>
        <mesh castShadow>
          <cylinderGeometry args={[0.7, 0.7, 0.5, 32]} rotation={[Math.PI / 2, 0, 0]} />
          <meshStandardMaterial
            color="#2a2a2a"
            metalness={0.85}
            roughness={0.15}
          />
        </mesh>

        {/* Shear line indicator */}
        <mesh position={[0, 0, 0.5]} castShadow>
          <planeGeometry args={[0.4, 1.4]} />
          <meshStandardMaterial
            color="#4a4a4a"
            metalness={0.5}
            roughness={0.5}
          />
        </mesh>
      </group>

      {/* Pin Stacks */}
      {[0, 1, 2, 3, 4].map((i) => {
        const xPos = -0.8 + i * 0.4;
        const isSet = lockState.pinsLifted[i];

        return (
          <group key={i} position={[xPos, 0.7, 0]}>
            {/* Housing Pin (bottom, fixed in housing) */}
            <mesh position={[0, -0.35, 0]} castShadow>
              <cylinderGeometry args={[0.09, 0.09, 0.5, 16]} />
              <meshStandardMaterial
                color="#d4a537"
                metalness={0.9}
                roughness={0.15}
              />
            </mesh>

            {/* Driver Pin (top, moves with picker) */}
            <mesh
              position={[0, isSet ? 0.5 : 0.15, 0]}
              castShadow
            >
              <cylinderGeometry args={[0.085, 0.085, 0.3, 16]} />
              <meshStandardMaterial
                color={isSet ? '#22c55e' : '#888888'}
                metalness={1}
                roughness={0.05}
                emissive={isSet ? '#16a34a' : '#000000'}
                emissiveIntensity={isSet ? 0.4 : 0}
              />
            </mesh>

            {/* Spring (animates based on pin position) */}
            <mesh position={[0, 0.6, 0]} castShadow>
              <cylinderGeometry args={[0.065, 0.065, 0.2, 8]} />
              <meshStandardMaterial
                color="#c41e3a"
                metalness={0.7}
                roughness={0.3}
              />
            </mesh>

            {/* Pin guide walls */}
            <mesh position={[0.12, 0, 0]} castShadow>
              <boxGeometry args={[0.02, 0.8, 0.15]} />
              <meshStandardMaterial color="#1a1a1a" />
            </mesh>
            <mesh position={[-0.12, 0, 0]} castShadow>
              <boxGeometry args={[0.02, 0.8, 0.15]} />
              <meshStandardMaterial color="#1a1a1a" />
            </mesh>
          </group>
        );
      })}

      {/* Keyway walls */}
      <mesh position={[-0.5, 0, 0.5]} castShadow>
        <boxGeometry args={[1.8, 0.15, 0.3]} />
        <meshStandardMaterial color="#1a1a1a" metalness={0.7} roughness={0.3} />
      </mesh>
      <mesh position={[-0.5, 0, -0.5]} castShadow>
        <boxGeometry args={[1.8, 0.15, 0.3]} />
        <meshStandardMaterial color="#1a1a1a" metalness={0.7} roughness={0.3} />
      </mesh>

      {/* Keyway slot walls */}
      <mesh position={[-0.5, -0.2, 0.6]} castShadow>
        <boxGeometry args={[1.8, 0.2, 0.25]} />
        <meshStandardMaterial color="#1a1a1a" metalness={0.6} roughness={0.4} />
      </mesh>
      <mesh position={[-0.5, -0.2, -0.6]} castShadow>
        <boxGeometry args={[1.8, 0.2, 0.25]} />
        <meshStandardMaterial color="#1a1a1a" metalness={0.6} roughness={0.4} />
      </mesh>

      {/* Shear line visual guide */}
      <mesh position={[0, 0.85, 0]} castShadow>
        <boxGeometry args={[2.0, 0.08, 0.02]} />
        <meshStandardMaterial
          color="#ff6b6b"
          emissive="#ff6b6b"
          emissiveIntensity={0.3}
        />
      </mesh>
    </group>
  );
}